import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { messageFromError } from "../../../shared/api/drfErrors";
import usePropertyMediaQueue from "../../../shared/propertyMediaQueue/usePropertyMediaQueue";
import {
  MEDIA_KIND_PHOTO,
  MEDIA_KIND_PLAN,
} from "../../../shared/propertyMediaQueue/propertyMediaQueue";
import PropertyMediaBoard from "../../../shared/propertyMediaManager/PropertyMediaBoard";
import {
  PROPERTY_MEDIA_MANAGER,
  REORDER_ERROR_MESSAGE,
  sortPropertyImages,
} from "../../../shared/propertyMediaManager/propertyMediaOrder";
import { usePropertyMediaReorder } from "../../../shared/propertyMediaManager/usePropertyMediaReorder";
import { usePropertyMediaUploadScheduler } from "../../../shared/propertyMediaManager/usePropertyMediaUploadScheduler";
import {
  deletePropertyImage,
  patchPropertyImage,
  reorderPropertyImages,
  uploadPropertyImages,
  propertyImageUploadErrorMessage,
} from "../api/realty";

function useCoalescedRefresh(callback) {
  const timerRef = useRef(null);
  const cbRef = useRef(callback);
  const pendingRef = useRef(0);
  cbRef.current = callback;

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  return useCallback(() => {
    pendingRef.current += 1;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      timerRef.current = null;
      if (pendingRef.current === 0) return;
      pendingRef.current = 0;
      try {
        await cbRef.current?.();
      } catch (_err) {
        toast.error("Файл завантажено, але не вдалося оновити список.");
      }
    }, 40);
  }, []);
}

function EditSectionMedia({
  property,
  onMediaChanged,
  videoUrl,
  onVideoUrlChange,
  mediaApi = null,
  locked = false,
}) {
  const uploadFn = mediaApi?.uploadPropertyImages || uploadPropertyImages;
  const patchFn = mediaApi?.patchPropertyImage || patchPropertyImage;
  const deleteFn = mediaApi?.deletePropertyImage || deletePropertyImage;
  const reorderFn = mediaApi?.reorderPropertyImages || reorderPropertyImages;
  const uploadErrorMessage =
    mediaApi?.propertyImageUploadErrorMessage ||
    propertyImageUploadErrorMessage;
  const propertyId = property && property.id;
  const [images, setImages] = useState(() => sortPropertyImages((property && property.images) || []));
  const canonicalRef = useRef({
    image: images.filter((img) => img.kind === "image"),
    plan: images.filter((img) => img.kind === "plan"),
  });
  const [busy, setBusy] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [photosReorderError, setPhotosReorderError] = useState(null);
  const [plansReorderError, setPlansReorderError] = useState(null);
  const pendingItemsRef = useRef([]);
  pendingItemsRef.current = pendingItems;

  const photos = images.filter((img) => img.kind === "image");
  const plans = images.filter((img) => img.kind === "plan");
  const imagesKey = (property && property.images
    ? property.images
    : []
  )
    .map((img) => `${img.id}:${img.position}:${img.is_main}:${img.kind}`)
    .join(",");

  useEffect(() => {
    const next = sortPropertyImages((property && property.images) || []);
    setImages(next);
    canonicalRef.current = {
      image: next.filter((img) => img.kind === "image"),
      plan: next.filter((img) => img.kind === "plan"),
    };
  }, [imagesKey]);

  const mediaQueue = usePropertyMediaQueue({
    items: pendingItems,
    setItems: setPendingItems,
    enabled: Boolean(propertyId),
  });
  const clearQueueRef = useRef(mediaQueue.clearQueue);
  clearQueueRef.current = mediaQueue.clearQueue;

  useEffect(() => {
    clearQueueRef.current();
    setEditingItem(null);
    setPhotosReorderError(null);
    setPlansReorderError(null);
  }, [propertyId]);

  const requestRefresh = useCoalescedRefresh(onMediaChanged);

  const uploadOne = useCallback(async (id, file, kind) => {
    try {
      return await uploadFn(id, [file], kind);
    } catch (err) {
      throw new Error(uploadErrorMessage(err));
    }
  }, [uploadFn, uploadErrorMessage]);

  const onItemUploaded = useCallback(
    async (item, row) => {
      if (editingItem?.localId === item.localId) {
        setEditingItem(null);
      }
      setImages((prev) => {
        const others = prev.filter((img) => img.id !== row.id);
        return sortPropertyImages([...others, row]);
      });
      requestRefresh();
      toast.success(
        item.kind === MEDIA_KIND_PLAN ? "Планування додано." : "Фото додано.",
      );
    },
    [editingItem, requestRefresh],
  );

  const scheduler = usePropertyMediaUploadScheduler({
    propertyId,
    items: pendingItems,
    mediaQueue,
    uploadOne,
    onItemUploaded,
    enabled: Boolean(propertyId),
  });

  const applyKindRows = useCallback((kind, rows) => {
    const ordered = sortPropertyImages(rows || []);
    canonicalRef.current[kind] = ordered;
    setImages((prev) => {
      const others = prev.filter((img) => img.kind !== kind);
      if (kind === "image") {
        return [...ordered, ...others.filter((img) => img.kind === "plan")];
      }
      return [...prev.filter((img) => img.kind === "image"), ...ordered];
    });
  }, []);

  const reorder = usePropertyMediaReorder({
    propertyId,
    reorderRequest: reorderFn,
    onCanonical: (kind, result) => {
      applyKindRows(kind, (result && result.images) || []);
      if (kind === "image") setPhotosReorderError(null);
      else setPlansReorderError(null);
    },
    onFailure: (kind) => {
      applyKindRows(kind, canonicalRef.current[kind] || []);
      if (kind === "image") setPhotosReorderError(REORDER_ERROR_MESSAGE);
      else setPlansReorderError(REORDER_ERROR_MESSAGE);
    },
  });

  const handleReorder = (kind, ids) => {
    const source = kind === "image" ? photos : plans;
    const byId = new Map(source.map((img) => [img.id, img]));
    const nextIds = Array.isArray(ids) ? [...ids] : [];
    const nextRows = nextIds.map((id) => byId.get(id)).filter(Boolean);
    setImages((prev) => {
      const others = prev.filter((img) => img.kind !== kind);
      return kind === "image"
        ? [...nextRows, ...others]
        : [...others, ...nextRows];
    });
    reorder.enqueue(kind, nextIds);
  };

  const runMutation = async (fn, successMessage) => {
    if (busy || !propertyId) return;
    setBusy(true);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      if (onMediaChanged) await onMediaChanged();
    } catch (err) {
      toast.error(messageFromError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleMakeMain = (imageId) =>
    runMutation(async () => {
      const updated = await patchFn(propertyId, imageId, {
        is_main: true,
      });
      setImages((prev) => {
        const photosNext = prev.filter((img) => img.kind === "image");
        const selected = photosNext.find((img) => img.id === imageId);
        if (!selected) return prev;
        const remainder = photosNext.filter((img) => img.id !== imageId);
        const ordered = [
          {
            ...selected,
            ...(updated && updated.id === imageId ? updated : {}),
            is_main: true,
            position: 0,
          },
          ...remainder.map((img, index) => ({
            ...img,
            is_main: false,
            position: index + 1,
          })),
        ];
        canonicalRef.current.image = ordered;
        return [...ordered, ...prev.filter((img) => img.kind === "plan")];
      });
    }, "Головне зображення оновлено.");

  const handleDeleteServer = (imageId) =>
    runMutation(async () => {
      await deleteFn(propertyId, imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    }, "Зображення видалено.");

  const handleRemovePending = (localId) => {
    if (editingItem?.localId === localId) {
      setEditingItem(null);
    }
    mediaQueue.removeItem(localId);
  };

  const handleReplaceServer = async (imageId, file) => {
    if (busy || !propertyId) return;
    setBusy(true);
    try {
      const updated = await patchFn(propertyId, imageId, {
        image: file,
      });
      setImages((prev) =>
        sortPropertyImages(
          prev.map((img) => (img.id === imageId ? { ...img, ...updated } : img)),
        ),
      );
      setEditingItem(null);
      toast.success("Зображення оновлено.");
      requestRefresh();
    } catch (err) {
      toast.error(messageFromError(err) || "Не вдалося оновити зображення.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="sec-media"
      className="ap-pane"
      aria-labelledby="ap-pane-media"
      data-property-media-manager={PROPERTY_MEDIA_MANAGER}
    >
      <header className="ap-pane__head">
        <h2 id="ap-pane-media">
          <i className="fa-solid fa-camera" aria-hidden="true" />
          Фото та медіа
        </h2>
        <p>Фотографії, планування та відео об'єкта.</p>
      </header>

      <PropertyMediaBoard
        kind="image"
        title="Фото об'єкта"
        dropTitle="Додати ще фото"
        inputId="ap-photos"
        serverItems={photos}
        localItems={mediaQueue.photos}
        busy={busy || locked}
        canReorder={Boolean(propertyId)}
        allowMakeMain
        reorderError={photosReorderError}
        onAdd={(files) => mediaQueue.addFiles(files, MEDIA_KIND_PHOTO)}
        onReorder={(ids) => handleReorder("image", ids)}
        onMakeMain={handleMakeMain}
        onDeleteServer={handleDeleteServer}
        onDeleteLocal={handleRemovePending}
        onRetryPrepare={mediaQueue.retryPrepare}
        onRetryUpload={scheduler.retryUpload}
        onEditItem={setEditingItem}
        editingItem={editingItem}
        onApplyEdit={(localId, file) => {
          scheduler.forgetAttempts(localId);
          mediaQueue.applyEditedFile(localId, file);
          setEditingItem(null);
        }}
        onReplaceServer={handleReplaceServer}
        onCancelEdit={() => setEditingItem(null)}
      />

      <PropertyMediaBoard
        kind="plan"
        title="Планування"
        dropTitle="Додати ще план"
        inputId="ap-plans"
        serverItems={plans}
        localItems={mediaQueue.plans}
        busy={busy || locked}
        canReorder={Boolean(propertyId)}
        allowMakeMain={false}
        reorderError={plansReorderError}
        onAdd={(files) => mediaQueue.addFiles(files, MEDIA_KIND_PLAN)}
        onReorder={(ids) => handleReorder("plan", ids)}
        onDeleteServer={handleDeleteServer}
        onDeleteLocal={handleRemovePending}
        onRetryPrepare={mediaQueue.retryPrepare}
        onRetryUpload={scheduler.retryUpload}
        onEditItem={setEditingItem}
        editingItem={editingItem}
        onApplyEdit={(localId, file) => {
          scheduler.forgetAttempts(localId);
          mediaQueue.applyEditedFile(localId, file);
          setEditingItem(null);
        }}
        onReplaceServer={handleReplaceServer}
        onCancelEdit={() => setEditingItem(null)}
      />

      <div className="ap-row">
        <div className="ap-field ap-field--wide">
          <label htmlFor="prop-video">
            <i
              className="fa-brands fa-youtube visibility-icon"
              aria-hidden="true"
            />
            Відео (YouTube)
          </label>
          <input
            id="prop-video"
            name="video_url"
            type="url"
            className="form-control"
            placeholder="https://www.youtube.com/watch?v=…"
            value={videoUrl || ""}
            onChange={(e) => onVideoUrlChange(e.target.value)}
          />
          <small className="form-hint">
            Необов'язково. Стандартне посилання YouTube.
          </small>
        </div>
      </div>
    </section>
  );
}

export default EditSectionMedia;
